import { useEffect, useState, type MouseEvent, type ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh, faXmark } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { formatRelativeTime } from "@/components/missions/MissionRow";
import type { Audit } from "@/services/api/guardiansv2/types";
import { useOsNavigate } from "@/components/ui/os-layout";
import { ShareButton } from "@/components/ui/side-panel";
import { RatingBadge } from "../shared/RatingBadge";
import { AuditTypeBadge } from "../shared/AuditTypeBadge";
import { PersonaPanel } from "./PersonaPanel";
import { AuditReportPanel } from "./AuditReportPanel";
import { AuditInspectorContext } from "./AuditInspectorContext";
import { AuditRawInputs } from "./AuditRawInputs";
import {
  AuditPersonaTabs,
  computeTabVisualState,
  defaultPersonaTab,
  type AuditPersonaTabId,
} from "./AuditPersonaTabs";
import {
  AuditPersonaRerunActions,
  type PersonaRerunAction,
} from "./AuditPersonaRerunActions";
import {
  entityDeepLink,
  entityRefFromAudit,
  entityRefFromSubject,
} from "../shared/entityDeepLink";
import { useGuardiansAuditLaunch } from "../auditLaunch/GuardiansAuditLaunchContext";
import { isAuditPending } from "../auditLaunch/auditPending";
import type { ScopeLabelCache } from "./useScopeLabelCache";
import { LinkedTicketCard } from "./LinkedTicketCard";
import { isIssueFirstAudit, resolveSynthesisBody } from "./auditPresentation";
import { entityTextScopeFromAuditScope } from "../shared/entityTags";
import { EntityRichText } from "../shared/EntityRichText";
import { AuditTriggeredBySection } from "./AuditTriggeredBySection";
import { auditConversationScope } from "./auditConversationScope";
import { useOpenGuardiansConversation } from "../conversation/useOpenGuardiansConversation";

export interface AuditInspectorProps {
  audit: Audit | null;
  loading: boolean;
  onClose: () => void;
  onRefresh?: () => void | Promise<void>;
  onRerunComplete?: () => void;
  scopeLabels: ScopeLabelCache;
  /** Deep link copied by the Share control; omit to hide Share. */
  shareUrl?: string;
  /** Pinned footer (e.g. link to the audited entity on the timeline). */
  footer?: ReactNode;
  /** Navigate to the rules editor for a failed check. */
  onEditRule?: (catalogueKey: string, ruleId: string) => void;
  /** Shown when there is no audit document yet (e.g. trigger not yet audited). */
  emptyTitle?: string;
}

export function AuditInspector({
  audit,
  loading,
  onClose,
  onRefresh,
  onRerunComplete,
  scopeLabels,
  shareUrl,
  footer,
  emptyTitle,
  onEditRule,
}: AuditInspectorProps) {
  const osNavigate = useOsNavigate();
  const openConversation = useOpenGuardiansConversation();
  const { isAuditInFlight } = useGuardiansAuditLaunch();
  const [personaTab, setPersonaTab] = useState<AuditPersonaTabId>("synthesis");
  const [refreshing, setRefreshing] = useState(false);
  const [personaRerunAction, setPersonaRerunAction] = useState<PersonaRerunAction | null>(null);
  const [rerunAnchorTab, setRerunAnchorTab] = useState<AuditPersonaTabId | null>(null);

  useEffect(() => {
    if (audit) setPersonaTab(defaultPersonaTab(audit));
  }, [audit]);

  if (loading) {
    return (
      <div
        className="flex h-full items-center justify-center text-slate-400"
        data-testid="audit-inspector-loading"
      >
        Loading…
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="flex h-full flex-col overflow-hidden" data-testid="audit-inspector-empty">
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-white/5 px-6 py-4">
          <h2 className="min-w-0 truncate text-lg font-semibold text-slate-100">
            {emptyTitle ?? "Audit"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-slate-200"
            aria-label="Close"
            data-testid="audit-inspector-close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </header>
        <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-slate-400">
          No audit has been run for this item yet.
        </div>
        {footer ? (
          <footer
            className="shrink-0 border-t border-white/5 px-6 py-4"
          >
            {footer}
          </footer>
        ) : null}
      </div>
    );
  }

  const title = audit.inputs?.summary?.trim() || audit.audit_type;
  const ref = entityRefFromAudit(audit);
  const deepLink = ref ? entityDeepLink(ref) : null;
  const trackedInFlight = isAuditInFlight(audit._id);
  const pending = isAuditPending(audit, trackedInFlight);
  const tabProgress = {
    auditRerunning: trackedInFlight,
    personaRerunAction,
    rerunAnchorTab,
  };
  const activeTabRunning =
    computeTabVisualState(audit, personaTab, tabProgress) === "running";
  const synthesisRunning =
    computeTabVisualState(audit, "synthesis", tabProgress) === "running";
  const issueFirst = isIssueFirstAudit(audit);
  const conversationScope = auditConversationScope(audit);
  const canOpenConversation = Boolean(conversationScope.missionId || conversationScope.taskId);
  const entityScope = entityTextScopeFromAuditScope(audit.scope);

  const personaMain = (
    <>
      <AuditPersonaTabs
        audit={audit}
        activeId={personaTab}
        onChange={setPersonaTab}
        progress={tabProgress}
      />
      <div className="mt-3">
        {personaTab === "synthesis" ? (
          synthesisRunning ? (
            <div
              className="rounded-lg border border-indigo-500/25 bg-indigo-500/10 px-4 py-6 text-center"
              data-testid="synthesis-panel-running"
            >
              <p className="text-sm font-medium text-indigo-200">Synthesis in progress…</p>
              <p className="mt-1 text-xs text-indigo-300/80">
                Aggregating Guardian verdicts into an executive summary.
              </p>
            </div>
          ) : (
            <div className="text-sm leading-relaxed text-slate-300">
              <EntityRichText
                text={resolveSynthesisBody(audit) || "No synthesis summary yet."}
                scope={entityScope}
                scopeLabels={scopeLabels}
              />
            </div>
          )
        ) : (
          <PersonaPanel
            result={audit.personas[personaTab]}
            label={personaTab}
            running={activeTabRunning}
            entityScope={entityScope}
            scopeLabels={scopeLabels}
          />
        )}
      </div>
    </>
  );

  const guardianAnalysisCollapsible = (
    <details className="group mt-6 border-t border-white/5 pt-4" data-testid="guardian-analysis">
      <summary className="cursor-pointer list-none text-xs font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-200">
        Guardian analysis (Ace / Ivy / Nova)
      </summary>
      <div className="mt-3">{personaMain}</div>
    </details>
  );

  const unitRef = audit.unit_of_work
    ? entityRefFromSubject(audit.unit_of_work, audit.scope)
    : null;
  const unitLink = unitRef ? entityDeepLink(unitRef) : null;
  const unitOfWorkAction = unitLink ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        unitLink.run({ osNavigate });
      }}
      className="text-xs font-medium text-indigo-300 hover:text-indigo-200"
      data-testid="audit-unit-of-work-link"
    >
      View full context ({unitRef?.kind}) →
    </button>
  ) : null;

  const openSubject = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    deepLink?.run({ osNavigate });
  };

  const handleRefresh = async () => {
    if (!onRefresh) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden" data-testid="audit-inspector">
      <header className="shrink-0 border-b border-white/5 px-6 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <h2 className="text-lg font-semibold text-slate-100">{title}</h2>
              <AuditTypeBadge
                auditType={audit.audit_type}
                onClick={deepLink ? openSubject : undefined}
              />
              <RatingBadge rating={audit.rating} pending={pending} />
            </div>
            <p className="mt-2 text-xs text-slate-400">
              {formatRelativeTime(audit.updated_at || audit.completed_at || audit.created_at)}
              {audit.cost_usd != null ? ` · $${audit.cost_usd.toFixed(4)}` : ""}
              {audit.duration_ms ? ` · ${(audit.duration_ms / 1000).toFixed(1)}s` : ""}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {shareUrl ? (
              <ShareButton url={shareUrl} label="Copy link to this audit" />
            ) : null}
            {onRefresh ? (
              <button
                type="button"
                onClick={() => void handleRefresh()}
                disabled={refreshing}
                className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-slate-200 disabled:opacity-50"
                data-testid="audit-refresh"
                aria-label="Refresh audit"
              >
                <FontAwesomeIcon icon={faRefresh} className={refreshing ? "animate-spin" : ""} />
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-slate-200"
              aria-label="Close"
              data-testid="audit-inspector-close"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 overflow-hidden lg:grid-cols-[minmax(340px,40rem)_minmax(260px,1fr)]">
        <div className="flex min-h-0 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            {issueFirst ? (
              <>
                <AuditTriggeredBySection audit={audit} onEditRule={onEditRule} />
                <AuditReportPanel
                  audit={audit}
                  running={synthesisRunning}
                  unitOfWorkAction={unitOfWorkAction}
                  scopeLabels={scopeLabels}
                />
                {guardianAnalysisCollapsible}
                {canOpenConversation ? (
                  <div className="mt-6 border-t border-white/5 pt-4">
                    <button
                      type="button"
                      onClick={() =>
                        openConversation({
                          missionId: conversationScope.missionId,
                          taskId: conversationScope.taskId,
                          title: audit.screening_snapshot?.alert_title ?? title,
                        })
                      }
                      className="rounded-lg bg-slate-800 px-3 py-2 text-sm text-slate-200 hover:bg-slate-700"
                      data-testid="audit-view-conversation"
                    >
                      View conversation
                    </button>
                  </div>
                ) : null}
              </>
            ) : (
              <section data-testid="audit-entity-layout">{personaMain}</section>
            )}

            {audit.ticket_ids.length > 0 ? (
              <section className="mt-6 border-t border-white/5 pt-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Linked tickets
                </h3>
                <ul className="mt-2 space-y-2">
                  {audit.ticket_ids.map((ref) => (
                    <li key={ref}>
                      <LinkedTicketCard
                        ticketRef={ref}
                        onOpen={() =>
                          osNavigate(`/ops/guardians/tickets?ticket=${encodeURIComponent(ref)}`)
                        }
                      />
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {!issueFirst && unitOfWorkAction ? (
              <div className="mt-6 border-t border-white/5 pt-4">{unitOfWorkAction}</div>
            ) : null}

            <AuditRawInputs
              audit={audit}
              actions={
                <AuditPersonaRerunActions
                  auditId={audit._id}
                  personaTab={personaTab}
                  disabled={refreshing || personaRerunAction != null}
                  activeAction={personaRerunAction}
                  onActionStart={(action) => {
                    setPersonaRerunAction(action);
                    setRerunAnchorTab(personaTab);
                  }}
                  onActionEnd={() => {
                    setPersonaRerunAction(null);
                    setRerunAnchorTab(null);
                  }}
                  onComplete={onRerunComplete}
                />
              }
            />
          </div>
        </div>

        <div className="overflow-y-auto border-t border-white/5 px-6 py-4 lg:border-l lg:border-t-0">
          <AuditInspectorContext audit={audit} scopeLabels={scopeLabels} />
        </div>
      </div>

      {footer ? (
        <footer
          className="shrink-0 border-t border-white/5 px-6 py-3"
          data-testid="audit-inspector-footer"
        >
          {footer}
        </footer>
      ) : null}
    </div>
  );
}
